import { nameof } from 'ts-simple-nameof';
import * as yup from 'yup';
import { PersonDto, PolicyHolderAdditionalData, PolicyHolderDto } from '../dtos';
import { Selection } from '../enums';
import { AddressValidationSchema, Step4AddressValidationSchema } from './address';

const typeRequiredSchemaOpt = (expectedType: Selection, message: string) => {
    return {
        is: (givenType: string) => givenType && [expectedType, Selection.SelfEmployed].includes(parseInt(givenType)),
        then: () => yup.string().required(message).nullable(),
        otherwise: () => yup.string().nullable()
    }
}

export const PersonValidationSchema = {
    type: yup.number().required('Vyberte typ osoby'),
    name: yup.string().when(nameof<PersonDto>(x => x.type), typeRequiredSchemaOpt(Selection.Person, 'Zadejte jméno')),
    surname: yup.string().when(nameof<PersonDto>(x => x.type), typeRequiredSchemaOpt(Selection.Person, 'Zadejte příjmení')),
    idendificationNumber: yup.string().when(nameof<PersonDto>(x => x.type), typeRequiredSchemaOpt(Selection.Company, 'Zadejte IČO')),
    companyName: yup.string().when(nameof<PersonDto>(x => x.type), typeRequiredSchemaOpt(Selection.Company, 'Zadejte název')),
    birthNumber: yup.string().when([nameof<PersonDto>(x => x.type), nameof<PersonDto>(x => x.isForeigner)], {
        is: (givenType: string, isForeigner: boolean | undefined) => givenType &&  //z K4 se někdy vrací isForeigner jako undefined i když už je na K1 rč nebo datum narození uloženo
            isForeigner !== undefined && !isForeigner && [Selection.Person, Selection.SelfEmployed].includes(parseInt(givenType)),
        then: () => yup.string().nullable().required('Zadejte rodné číslo'),
        otherwise: () => yup.string().nullable()
    }),
    birthday: yup.string().when([nameof<PersonDto>(x => x.type), nameof<PersonDto>(x => x.isForeigner)], {
        is: (givenType: string, isForeigner: boolean | undefined) => givenType &&
            isForeigner !== undefined && isForeigner && [Selection.Person, Selection.SelfEmployed].includes(parseInt(givenType)),
        then: () => yup.string().required('Zadejte datum narození'),
        otherwise: () => yup.string().nullable()
    }),
    address: yup.object({ ...AddressValidationSchema }),
}

const personSameAsDependentRequiredSchema = {
    is: (sameAs: any) => sameAs === Selection.PersonSameAsPolicyHolder || sameAs === Selection.PersonSameAsKeeper,
    then: () => yup.object().optional().nullable(),
    otherwise: () => yup.object({ ...PersonValidationSchema }).nullable()
}

export const PolicyHolderValidationRuleObject = {
    person: yup.object({ ...PersonValidationSchema }),
    holder: yup.object().when(nameof<PolicyHolderDto>(x => x.holderSameAs), personSameAsDependentRequiredSchema),
    owner: yup.object().when(nameof<PolicyHolderDto>(x => x.ownerSameAs), personSameAsDependentRequiredSchema),
}

export const PolicyHolderPropertyValidationRuleObject = {
    person: yup.object({
        ...PersonValidationSchema,
        address: yup.object().optional().nullable(),
    }),
    insuredPerson: yup.object().when(nameof<PolicyHolderDto>(x => x.insuredPersonSameAs), {
        is: (sameAs: any) => sameAs === Selection.PersonSameAsPolicyHolder,
        then: () => yup.object().optional().nullable(),
        otherwise: () => yup.object({
            ...PersonValidationSchema,
            address: yup.object().optional().nullable(),
        }).nullable()
    }),
}

export const ContactPersonValidationSchema = {
    type: yup.number().required('Vyberte typ osoby'),
    name: yup.string().when(nameof<PersonDto>(x => x.type), typeRequiredSchemaOpt(Selection.Person, 'Zadejte jméno')),
    surname: yup.string().when(nameof<PersonDto>(x => x.type), typeRequiredSchemaOpt(Selection.Person, 'Zadejte příjmení')),
    companyName: yup.string().when(nameof<PersonDto>(x => x.type), typeRequiredSchemaOpt(Selection.Company, 'Zadejte název')),
    address: yup.object({ ...Step4AddressValidationSchema }),
}

export const PolicyHolderPropertyStep4ValidationRuleObject = {
    person: yup.object({
        ...PersonValidationSchema,
        address: yup.object({ ...Step4AddressValidationSchema }),
    }),
    insuredPerson: yup.object().when(nameof<PolicyHolderDto>(x => x.insuredPersonSameAs), {
        is: (sameAs: any) => sameAs === Selection.PersonSameAsPolicyHolder,
        then: () => yup.object().optional().nullable(),
        otherwise: () => yup.object({
            ...PersonValidationSchema,
            address: yup.object({ ...Step4AddressValidationSchema }),
        }).nullable()
    }),
    contact: yup.object().when(nameof<PolicyHolderAdditionalData>(x => x.contactSameAs), {
        is: (sameAs: any) => sameAs === Selection.PersonSameAsPolicyHolder
            || sameAs === Selection.PersonSameAsClient,
        then: () => yup.object().optional().nullable(),
        otherwise: () => yup.object({
            ...ContactPersonValidationSchema
        }).nullable()
    }),
}

export const PolicyHolderValidationSchema = yup.object().shape(PolicyHolderValidationRuleObject) as any
export const PolicyHolderPropertyValidationSchema = yup.object().shape(PolicyHolderPropertyValidationRuleObject) as any
export const PolicyHolderPropertyStep4ValidationSchema = yup.object().shape(PolicyHolderPropertyStep4ValidationRuleObject) as any